import { offlineDb } from '@/db/offlineDb';
import { syncService } from '@/services/sync.service';
import { supabase } from '@/integrations/supabase/client';
import { networkService } from '@/services/network.service';
import OfflineLogger from '@/utils/offlineLogger';
import logger from '@/utils/logger';
import { categoryOperationsService } from '@/services/categories/categoryOperations.service';

class SyncDataIntegrityService {
  async validateAndCleanEntityData(entityName: string, userId: string): Promise<{
    localCount: number;
    remoteCount: number | null;
    cleanedDuplicates: number;
    errors: string[];
  }> {
    const errors: string[] = [];
    let cleanedDuplicates = 0;

    try {
      OfflineLogger.info('SyncDataIntegrityService', `Starting data validation for ${entityName}`);

      // 1. Clean local duplicates first
      cleanedDuplicates = await this.cleanLocalDuplicates(entityName, userId);

      // 2. Get clean counts
      const localCount = await this.getCleanLocalCount(entityName, userId);
      const remoteCount = await this.getRemoteCount(entityName, userId, errors);

      // 3. Validate data integrity
      await this.validateDataIntegrity(entityName, userId, errors);

      if (errors.length > 0) {
        for (const err of errors) {
          await logger.logError('sync_validation', `${entityName}: ${err}`);
        }
      }

      OfflineLogger.info('SyncDataIntegrityService', {
        entityName,
        localCount,
        remoteCount,
        cleanedDuplicates,
        errorsCount: errors.length
      });

      return {
        localCount,
        remoteCount,
        cleanedDuplicates,
        errors
      };
    } catch (error) {
      OfflineLogger.error('SyncDataIntegrityService.validateAndCleanEntityData', error);
      await logger.logError('sync_validation', `Validation failed for ${entityName}: ${error}`);
      errors.push(`Validation failed: ${error}`);
      return {
        localCount: 0,
        remoteCount: null,
        cleanedDuplicates,
        errors
      };
    }
  }

  private async cleanLocalDuplicates(entityName: string, userId: string): Promise<number> {
    try {
      let cleanedCount = 0;

      switch (entityName) {
        case 'shoppingItems':
          // For shopping items, remove duplicates by list_id + product_id
          const items = await offlineDb.shoppingItems.toArray();
          const itemsMap = new Map<string, any>();
          
          for (const item of items) {
            const key = `${item.list_id}-${item.product_id}`;
            if (itemsMap.has(key)) {
              // Keep the most recent one
              const existing = itemsMap.get(key);
              if (item._lastModified > existing._lastModified) {
                await offlineDb.shoppingItems.delete(existing.id);
                itemsMap.set(key, item);
                cleanedCount++;
              } else {
                await offlineDb.shoppingItems.delete(item.id);
                cleanedCount++;
              }
            } else {
              itemsMap.set(key, item);
            }
          }
          break;

        case 'categories':
        case 'products':
        case 'shops':
        case 'shoppingLists':
        case 'recurringProducts':
          // For other entities, remove duplicates by id within user
          const table = offlineDb[entityName];
          if (table) {
            const records = await table.where('user_id').equals(userId).toArray();
            const idsMap = new Map<string, any>();
            
            for (const record of records) {
              if (idsMap.has(record.id)) {
                // Keep the most recent one
                const existing = idsMap.get(record.id);
                if (record._lastModified > existing._lastModified) {
                  await table.delete(existing.id);
                  idsMap.set(record.id, record);
                  cleanedCount++;
                } else {
                  await table.delete(record.id);
                  cleanedCount++;
                }
              } else {
                idsMap.set(record.id, record);
              }
            }
          }
          break;

        case 'images':
          const images = await offlineDb.imageCache.toArray();
          const imageIds = new Set<string>();
          for (const img of images) {
            if (imageIds.has(img.id)) {
              await offlineDb.imageCache.delete(img.id);
              cleanedCount++;
            } else {
              imageIds.add(img.id);
            }
          }
          break;
      }

      if (cleanedCount > 0) {
        OfflineLogger.info('SyncDataIntegrityService', `Cleaned ${cleanedCount} duplicates for ${entityName}`);
      }

      return cleanedCount;
    } catch (error) {
      OfflineLogger.error('SyncDataIntegrityService.cleanLocalDuplicates', error);
      return 0;
    }
  }

  private async getCleanLocalCount(entityName: string, userId: string): Promise<number> {
    try {
      switch (entityName) {
        case 'shoppingItems':
          return await offlineDb.shoppingItems
            .where('_syncStatus')
            .notEqual('deleted')
            .count();

        case 'categories':
        case 'products':
        case 'shops':
        case 'shoppingLists':
        case 'recurringProducts':
          const table = offlineDb[entityName];
          if (table) {
            return await table
              .where('user_id')
              .equals(userId)
              .and(item => item._syncStatus !== 'deleted')
              .count();
          }
          return 0;

        case 'images':
          return await offlineDb.imageCache.count();

        default:
          return 0;
      }
    } catch (error) {
      OfflineLogger.error('SyncDataIntegrityService.getCleanLocalCount', error);
      return 0;
    }
  }

  private async getRemoteCount(
    entityName: string,
    userId: string,
    errors: string[]
  ): Promise<number | null> {
    if (!networkService.isOnline()) {
      errors.push('remote count unavailable');
      return null;
    }

    try {
      if (!networkService.isOnline()) {
        OfflineLogger.info('SyncDataIntegrityService', `Skipping remote count for ${entityName} - offline`);
        errors.push('remote count unavailable');
        return null;
      }
      const tableName = this.getTableName(entityName);

      let query;
      if (entityName === 'images') {
        query = supabase
          .from('user_products')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .not('photo', 'is', null);
      } else {
        query = supabase.from(tableName).select('*', { count: 'exact', head: true });

        // Add user filter for user-specific entities
        if (['user_categories', 'user_products', 'user_shops', 'shopping_lists', 'recurring_products'].includes(tableName)) {
          query = query.eq('user_id', userId);
        }
      }
      
      if (entityName === 'images') {
        query = query.not('photo', 'is', null);
      }

      const { count, error } = await query;

      if (error) {
        OfflineLogger.error('SyncDataIntegrityService.getRemoteCount', error);
        errors.push(error.message || 'remote count unavailable');
        return null;
      }

      return count ?? 0;
    } catch (error) {
      OfflineLogger.error('SyncDataIntegrityService.getRemoteCount', error);
      errors.push('remote count unavailable');
      return null;
    }
  }

  private async validateDataIntegrity(entityName: string, userId: string, errors: string[]): Promise<void> {
    try {
      // Check for records with invalid sync status
      let table: any = offlineDb[entityName];
      if (entityName === 'images') {
        table = offlineDb.imageCache;
      }
      if (table && entityName !== 'images') {
        const invalidRecords = await table
          .where('_syncStatus')
          .noneOf(['synced', 'pending', 'deleted'])
          .count();
        
        if (invalidRecords > 0) {
          errors.push(`${invalidRecords} records with invalid sync status in ${entityName}`);
        }
      }

      // Check for orphaned records (specific validations per entity)
      await this.validateEntitySpecificIntegrity(entityName, userId, errors);
    } catch (error) {
      OfflineLogger.error('SyncDataIntegrityService.validateDataIntegrity', error);
      errors.push(`Integrity validation failed for ${entityName}: ${error}`);
    }
  }

  private async validateEntitySpecificIntegrity(entityName: string, userId: string, errors: string[]): Promise<void> {
    try {
      switch (entityName) {
        case 'shoppingItems':
          // Check for items without valid list_id or product_id
          const invalidItems = await offlineDb.shoppingItems
            .where('list_id')
            .equals('')
            .or('product_id')
            .equals('')
            .count();
          
          if (invalidItems > 0) {
            errors.push(`${invalidItems} shopping items with missing list_id or product_id`);
          }
          break;

        case 'products':
          // Check for products without category and automatically fix them
          const uncategorized = await offlineDb.products
            .where('user_id')
            .equals(userId)
            .and(item => !item.category_id)
            .toArray();

          if (uncategorized.length > 0) {
            const defaultCategoryId = await this.ensureDefaultCategory(userId);
            await Promise.all(
              uncategorized.map(p =>
                offlineDb.products.update(p.id, {
                  category_id: defaultCategoryId,
                  _syncStatus: 'pending' as const,
                  _lastModified: Date.now()
                })
              )
            );

            OfflineLogger.warn(
              'SyncDataIntegrityService.validateEntitySpecificIntegrity',
              `${uncategorized.length} products without category corrected`
            );
          }
          break;
      }
    } catch (error) {
      OfflineLogger.error('SyncDataIntegrityService.validateEntitySpecificIntegrity', error);
    }
  }

  private async ensureDefaultCategory(userId: string): Promise<string> {
    let category = await offlineDb.categories
      .where('user_id')
      .equals(userId)
      .and(c => c.name === 'Senza Categoria')
      .first();

    if (!category) {
      const newId = crypto.randomUUID();
      const newCategory = {
        id: newId,
        user_id: userId,
        name: 'Senza Categoria',
        icon: '📋',
        color: '#f3f4f6',
        text_color: '#000000',
        position: 0,
        visibile: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        _syncStatus: 'pending' as const,
        _lastModified: Date.now()
      };

      try {
        await offlineDb.categories.put(newCategory);
        categoryOperationsService
          .createCategory({
            name: newCategory.name,
            icon: newCategory.icon,
            color: newCategory.color,
            text_color: newCategory.text_color
          })
          .catch(() => {});
        category = newCategory;
      } catch (error) {
        OfflineLogger.error('SyncDataIntegrityService.ensureDefaultCategory', error);
        throw error;
      }
    }

    return category.id;
  }

  private getTableName(entityName: string): string {
    const tableMapping: Record<string, string> = {
      'categories': 'user_categories',
      'products': 'user_products',
      'shops': 'user_shops',
      'shoppingLists': 'shopping_lists',
      'shoppingItems': 'shopping_items',
      'recurringProducts': 'recurring_products',
      'purchaseHistory': 'shopping_history',
      'images': 'user_products'
    };

    return tableMapping[entityName] || entityName;
  }

  async performFullDataAudit(userId: string): Promise<{
    totalLocalRecords: number;
    totalRemoteRecords: number;
    entitiesAudited: number;
    totalCleaned: number;
    allErrors: string[];
  }> {
    const entities = [
      'categories',
      'products',
      'shops',
      'shoppingLists',
      'shoppingItems',
      'recurringProducts',
      'images',
      'purchaseHistory'
    ];
    
    let totalLocalRecords = 0;
    let totalRemoteRecords = 0;
    let totalCleaned = 0;
    const allErrors: string[] = [];

    for (const entity of entities) {
      const result = await this.validateAndCleanEntityData(entity, userId);
      totalLocalRecords += result.localCount;
      totalRemoteRecords += result.remoteCount ?? 0;
      totalCleaned += result.cleanedDuplicates;
      allErrors.push(...result.errors);
    }

    OfflineLogger.info('SyncDataIntegrityService.performFullDataAudit', {
      totalLocalRecords,
      totalRemoteRecords,
      entitiesAudited: entities.length,
      totalCleaned,
      errorsCount: allErrors.length
    });

    return {
      totalLocalRecords,
      totalRemoteRecords,
      entitiesAudited: entities.length,
      totalCleaned,
      allErrors
    };
  }
}

export const syncDataIntegrityService = new SyncDataIntegrityService();
